// src/pages/ReviewFormPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { fetchReviewForm, submitReviewByToken } from '../services/reviewsAPI';

// ── Star Rating Input ────────────────────────────────────────────────────────
const StarInput = ({ value, onChange }) => {
  const [hovered, setHovered] = useState(0);
  return (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', margin: '8px 0' }}>
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          type="button"
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onChange(star)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 2,
            fontSize: 40, lineHeight: 1,
            color: star <= (hovered || value) ? '#FFD60A' : '#e0e0e0',
            transition: 'color 0.12s, transform 0.1s',
            transform: star <= (hovered || value) ? 'scale(1.12)' : 'scale(1)',
          }}
        >
          ★
        </button>
      ))}
    </div>
  );
};

// ── Category options — shown for BOTH buyer & seller, context-aware ─────────
const BUYER_CATEGORY_OPTIONS = [
  { value: '',                    label: 'Select a category (optional)' },
  { value: 'customer_service',     label: '🤝 Customer Service' },
  { value: 'quality_of_cars',      label: '🚗 Quality of Cars' },
  { value: 'value_added_services', label: '✨ Value Added Services' },
];

const SELLER_CATEGORY_OPTIONS = [
  { value: '',                    label: 'Select a category (optional)' },
  { value: 'customer_service',     label: '🤝 Customer Service' },
  { value: 'pricing_experience',   label: '💰 Pricing & Valuation' },
  { value: 'pickup_process',       label: '🚛 Pickup & Paperwork Process' },
];

const RATING_LABELS = { 1: 'Poor', 2: 'Fair', 3: 'Good', 4: 'Very Good', 5: 'Excellent!' };

// ── Static page shell — defined OUTSIDE the component so it never remounts ──
// (This was the root cause of the "click cursor between every letter" bug:
//  the old version defined <Wrapper> INSIDE the component body, which created
//  a brand-new component type on every keystroke re-render, forcing React to
//  unmount + remount the textarea — and with it, lose focus — after every char.)
const PageShell = ({ children }) => (
  <div style={{
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #003566 0%, #001d3d 60%, #000814 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 16, fontFamily: "'Segoe UI', system-ui, sans-serif",
  }}>
    <div style={{
      background: '#fff', borderRadius: 20, width: '100%', maxWidth: 480,
      boxShadow: '0 30px 80px rgba(0,0,0,0.4)',
      overflow: 'hidden',
    }}>
      {/* Brand strip */}
      <div style={{
        background: 'linear-gradient(135deg, #003566, #001d3d)',
        padding: '20px 28px',
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <div style={{
          width: 42, height: 42, borderRadius: 10,
          background: '#FFD60A', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 900, fontSize: 18, color: '#003566', flexShrink: 0,
        }}>
          CY
        </div>
        <div>
          <div style={{ color: '#FFD60A', fontWeight: 800, fontSize: 16 }}>CarYugo</div>
          <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>Customer Review</div>
        </div>
      </div>

      <div style={{ padding: '28px 28px 32px' }}>
        {children}
      </div>
    </div>
  </div>
);

// Global keyframes — injected ONCE at module load, not re-injected on every render
const GlobalStyles = () => (
  <style>{`
    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes pop { from { transform: scale(0.5); opacity: 0; } to { transform: scale(1); opacity: 1; } }
  `}</style>
);

export default function ReviewFormPage() {
  const { token } = useParams();

  const [state, setState] = useState('loading');   // loading | form | submitting | success | error
  const [formData, setFormData] = useState(null);  // data from server (customer_name, review_type)
  const [errMsg, setErrMsg]     = useState('');

  // Form fields
  const [rating,   setRating]   = useState(0);
  const [comment,  setComment]  = useState('');
  const [category, setCategory] = useState('');
  const [fieldErr, setFieldErr] = useState('');

  useEffect(() => {
    fetchReviewForm(token)
      .then(data  => { setFormData(data); setState('form'); })
      .catch(err  => { setErrMsg(err.message); setState('error'); });
  }, [token]);

  const handleSubmit = async () => {
    if (rating === 0) { setFieldErr('Please select a star rating.'); return; }
    if (comment.trim().length < 50) { setFieldErr('Please write at least 50 characters so we can understand your experience better.'); return; }
    setFieldErr('');
    setState('submitting');
    try {
      await submitReviewByToken(token, { rating, comment: comment.trim(), category: category || null });
      setState('success');
    } catch (e) {
      setErrMsg(e.message);
      setState('error');
    }
  };

  // ── Loading ──
  if (state === 'loading') return (
    <PageShell>
      <GlobalStyles />
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <div style={{
          width: 44, height: 44, border: '4px solid #e0e0e0',
          borderTopColor: '#FFD60A', borderRadius: '50%',
          animation: 'spin 0.8s linear infinite', margin: '0 auto 16px',
        }} />
        <p style={{ color: '#666', fontSize: 14 }}>Loading your review form…</p>
      </div>
    </PageShell>
  );

  // ── Error / expired ──
  if (state === 'error') return (
    <PageShell>
      <GlobalStyles />
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 56, marginBottom: 12 }}>😕</div>
        <h2 style={{ color: '#dc3545', fontSize: 20, marginBottom: 8 }}>Oops!</h2>
        <p style={{ color: '#555', fontSize: 14, lineHeight: 1.6 }}>{errMsg}</p>
        <p style={{ color: '#888', fontSize: 12, marginTop: 16 }}>
          If you think this is a mistake, please contact CarYugo support.
        </p>
      </div>
    </PageShell>
  );

  // ── Success ──
  if (state === 'success') return (
    <PageShell>
      <GlobalStyles />
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 64, marginBottom: 16, animation: 'pop 0.4s ease' }}>🎉</div>
        <h2 style={{ color: '#28a745', fontSize: 22, marginBottom: 8, fontWeight: 800 }}>
          Thank You!
        </h2>
        <p style={{ color: '#333', fontSize: 15, lineHeight: 1.7, marginBottom: 20 }}>
          Your review has been submitted successfully. Our team will review it and publish it shortly.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginBottom: 20 }}>
          {Array.from({ length: rating }).map((_, i) => (
            <span key={i} style={{ fontSize: 28, color: '#FFD60A' }}>★</span>
          ))}
        </div>
        <p style={{ color: '#888', fontSize: 12 }}>
          Have a great day! 🚗
        </p>
      </div>
    </PageShell>
  );

  // ── Form ──
  const isSeller = formData?.review_type === 'seller';
  const reviewTypeLabel = isSeller ? 'Selling Experience' : 'Buying Experience';
  const categoryOptions = isSeller ? SELLER_CATEGORY_OPTIONS : BUYER_CATEGORY_OPTIONS;

  return (
    <PageShell>
      <GlobalStyles />

      <h1 style={{ fontSize: 22, fontWeight: 800, color: '#003566', marginBottom: 4, marginTop: 0 }}>
        Share Your Experience
      </h1>
      <p style={{ color: '#666', fontSize: 14, marginBottom: 24, marginTop: 0 }}>
        Hi {formData?.customer_name || 'there'}! 👋 How was your <strong>{reviewTypeLabel}</strong> with CarYugo?
      </p>

      {/* Star rating */}
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 8 }}>
          Tap a star to rate your experience
        </div>
        <StarInput value={rating} onChange={setRating} />
        {rating > 0 && (
          <div style={{
            fontSize: 15, fontWeight: 700, marginTop: 8,
            color: rating >= 4 ? '#28a745' : rating === 3 ? '#856404' : '#dc3545',
          }}>
            {RATING_LABELS[rating]}
          </div>
        )}
      </div>

      {/* Category — now shown for BOTH buyer and seller, with context-aware options */}
      <div style={{ marginBottom: 18 }}>
        <label style={lblStyle}>What are you reviewing?</label>
        <select
          style={inputStyle}
          value={category}
          onChange={e => setCategory(e.target.value)}
        >
          {categoryOptions.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Comment */}
      <div style={{ marginBottom: 22 }}>
        <label style={lblStyle}>Your Review *</label>
        <textarea
          key="review-comment-textarea"
          style={{ ...inputStyle, minHeight: 130, resize: 'vertical' }}
          placeholder="Tell us about your experience with CarYugo in a few sentences. What did you like? What could be better? (minimum 50 characters)"
          value={comment}
          onChange={e => setComment(e.target.value)}
          autoFocus={false}
        />
        <div style={{
          fontSize: 11, marginTop: 4, textAlign: 'right',
          color: comment.trim().length >= 50 ? '#28a745' : '#aaa',
          fontWeight: comment.trim().length >= 50 ? 600 : 400,
        }}>
          {comment.length} characters (minimum 50) {comment.trim().length >= 50 ? '✓' : ''}
        </div>
      </div>

      {/* Field error */}
      {fieldErr && (
        <div style={{
          background: '#dc354510', border: '1px solid #dc354530',
          borderRadius: 8, padding: '9px 14px', color: '#dc3545',
          fontSize: 13, marginBottom: 16,
        }}>
          ⚠️ {fieldErr}
        </div>
      )}

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={state === 'submitting'}
        style={{
          width: '100%',
          background: state === 'submitting' ? '#ccc' : 'linear-gradient(135deg, #FFD60A, #FFC107)',
          color: '#003566',
          border: 'none',
          borderRadius: 10,
          padding: '14px',
          fontSize: 16,
          fontWeight: 800,
          cursor: state === 'submitting' ? 'not-allowed' : 'pointer',
          boxShadow: '0 4px 14px rgba(255,214,10,0.35)',
          transition: 'all 0.15s',
        }}
      >
        {state === 'submitting' ? 'Submitting…' : '⭐ Submit My Review'}
      </button>

      <p style={{ fontSize: 11, color: '#aaa', textAlign: 'center', marginTop: 16, marginBottom: 0 }}>
        Your review will appear on our website after approval.
      </p>
    </PageShell>
  );
}

const lblStyle   = { fontSize: 13, fontWeight: 600, color: '#444', display: 'block', marginBottom: 6 };
const inputStyle = {
  width: '100%', padding: '10px 14px',
  border: '1.5px solid #e0e0e0', borderRadius: 10,
  fontSize: 14, boxSizing: 'border-box',
  outline: 'none', fontFamily: 'inherit',
  transition: 'border-color 0.15s',
};